import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Box, Button, Tab, Tabs, TextField, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { MuiModal } from 'src/components/material/MuiModal';
import { User360Evaluation } from '../UsersTable';
import styles from './UserModal.module.scss';

type CreateUserInput = Omit<
  User360Evaluation,
  | 'id'
  | 'createdAt'
  | 'leaderLink'
  | 'employeeLink'
  | 'leaderDone'
  | 'answersCount'
>;

interface UserModalProps {
  isOpenModal: boolean;
  setIsOpenModal: (open: boolean) => void;
  user: User360Evaluation | null;
  isSubmitting?: boolean;
  onSubmit: (user: CreateUserInput) => void | Promise<void>;
  onImportSubmit: (users: CreateUserInput[]) => void | Promise<void>;
}

type ParsedImportRow = CreateUserInput & { sourceLine: number };

type ParsedImport = {
  validRows: ParsedImportRow[];
  totalLines: number;
  invalidLines: number;
  duplicateLines: number;
};

const EMAIL_REGEX = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i;

const cleanCell = (value: string) =>
  value
    .trim()
    .replace(/^['"]+|['"]+$/g, '')
    .trim();

const splitDelimitedLine = (line: string): string[] => {
  const delimiter = line.includes('\t')
    ? '\t'
    : line.split(';').length > line.split(',').length
      ? ';'
      : ',';

  if (!line.includes('\t') && !line.includes(';') && !line.includes(',')) {
    return [cleanCell(line)];
  }

  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && char === delimiter) {
      cells.push(cleanCell(current));
      current = '';
      continue;
    }

    current += char;
  }

  cells.push(cleanCell(current));
  return cells.filter(Boolean);
};

const extractEmail = (value: string): string | null => {
  const match = value.match(EMAIL_REGEX);
  if (!match) {
    return null;
  }
  return match[0].toLowerCase();
};

const removeEmailFromValue = (value: string) =>
  value
    .replace(EMAIL_REGEX, '')
    .replace(/[<>()[\]"]/g, '')
    .trim();

const looksLikeHeader = (cells: string[]) => {
  const normalized = cells.map((cell) => cleanCell(cell).toLowerCase());
  return (
    normalized.some(
      (cell) =>
        cell === 'email' ||
        cell.includes('email') ||
        cell.includes('e-post') ||
        /e[-\s]?post/.test(cell)
    ) &&
    normalized.some(
      (cell) =>
        cell === 'name' ||
        cell === 'user name' ||
        cell.includes('subject') ||
        cell.includes('navn')
    )
  );
};

const parsePastedUsers = (text: string): ParsedImport => {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const validRows: ParsedImportRow[] = [];
  const seenEmails = new Set<string>();
  let invalidLines = 0;
  let duplicateLines = 0;

  lines.forEach((line, index) => {
    const sourceLine = index + 1;
    const cells = splitDelimitedLine(line);

    if (cells.length === 0) {
      return;
    }

    if (index === 0 && looksLikeHeader(cells)) {
      return;
    }

    const emailCell = cells.find((cell) => extractEmail(cell) !== null);
    const extractedEmail = emailCell ? extractEmail(emailCell) : null;
    if (!extractedEmail) {
      invalidLines += 1;
      return;
    }

    if (seenEmails.has(extractedEmail)) {
      duplicateLines += 1;
      return;
    }

    const nameCandidates = cells
      .filter((cell) => cell !== emailCell)
      .map((cell) => removeEmailFromValue(cell))
      .filter(Boolean);

    const embeddedNameFromEmailCell = emailCell
      ? removeEmailFromValue(emailCell)
      : '';

    const name = [...nameCandidates, embeddedNameFromEmailCell]
      .filter(Boolean)
      .join(' ')
      .trim();

    if (!name) {
      invalidLines += 1;
      return;
    }

    seenEmails.add(extractedEmail);
    validRows.push({
      sourceLine,
      subjectName: name,
      subjectEmail: extractedEmail,
    });
  });

  return {
    validRows,
    totalLines: lines.length,
    invalidLines,
    duplicateLines,
  };
};

export const UserModal: FC<UserModalProps> = ({
  isOpenModal,
  setIsOpenModal,
  user,
  isSubmitting = false,
  onSubmit,
  onImportSubmit,
}) => {
  const [subjectName, setSubjectName] = useState<string>('');
  const [subjectEmail, setSubjectEmail] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'single' | 'import'>('single');
  const [csvText, setCsvText] = useState('');

  const parsedImport = useMemo(() => parsePastedUsers(csvText), [csvText]);

  useEffect(() => {
    if (!isOpenModal) {
      setSubjectName('');
      setSubjectEmail('');
      setCsvText('');
      setActiveTab('single');
      return;
    }

    if (user) {
      setSubjectName(user.subjectName);
      setSubjectEmail(user.subjectEmail);
      setCsvText('');
      setActiveTab('single');
    } else {
      setSubjectName('');
      setSubjectEmail('');
    }
  }, [isOpenModal, user]);

  const handleSubmit = () => {
    if (!subjectName.trim() || !subjectEmail.trim()) {
      alert('Please fill in name and email');
      return;
    }

    onSubmit({
      subjectName: subjectName.trim(),
      subjectEmail: subjectEmail.trim().toLowerCase(),
    });
  };

  const handleImportSubmit = () => {
    if (parsedImport.validRows.length === 0) {
      alert('No valid users found in the pasted text.');
      return;
    }

    onImportSubmit(
      parsedImport.validRows.map(({ sourceLine, ...row }) => ({
        ...row,
      }))
    );
  };

  const previewRows = parsedImport.validRows.slice(0, 8);

  return (
    <MuiModal open={isOpenModal} setOpen={setIsOpenModal} width={520}>
      <div className={styles.container}>
        <Typography variant="h6" className={styles.title}>
          {user ? 'Edit user' : 'Add user'}
        </Typography>

        {!user && (
          <Box className={styles.tabsWrap}>
            <Tabs
              value={activeTab}
              onChange={(_, value: 'single' | 'import') => setActiveTab(value)}
              variant="fullWidth"
            >
              <Tab label="Add user" value="single" />
              <Tab label="Import users" value="import" />
            </Tabs>
          </Box>
        )}

        {(user || activeTab === 'single') && (
          <div className={styles.singlePane}>
            <TextField
              label="User name"
              variant="outlined"
              value={subjectName}
              onChange={(e) => setSubjectName(e.target.value)}
              sx={{ width: '100%' }}
            />

            <TextField
              label="User email"
              variant="outlined"
              value={subjectEmail}
              onChange={(e) => setSubjectEmail(e.target.value)}
              sx={{ width: '100%' }}
            />

            <Button
              type="submit"
              variant="contained"
              sx={{ mt: 1, mb: 1, width: '100%' }}
              size="large"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Saving...'
                : user
                  ? 'Save changes'
                  : 'Create user'}
            </Button>
          </div>
        )}

        {!user && activeTab === 'import' && (
          <div className={styles.importPane}>
            <TextField
              label="Paste CSV text (name,email or email,name)"
              variant="outlined"
              multiline
              minRows={7}
              maxRows={10}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              sx={{
                width: '100%',
                '& .MuiInputBase-inputMultiline': {
                  overflowY: 'auto',
                },
              }}
            />

            {parsedImport.totalLines > 0 && (
              <Box className={styles.summary}>
                <div className={styles.summaryRow}>
                  <CheckCircleOutlinedIcon sx={{ color: '#16a34a' }} />
                  <Typography variant="body2">
                    {parsedImport.validRows.length} users ready to import
                  </Typography>
                </div>

                {(parsedImport.invalidLines > 0 ||
                  parsedImport.duplicateLines > 0) && (
                  <div className={styles.summaryRow}>
                    <WarningAmberOutlinedIcon sx={{ color: '#d97706' }} />
                    <Typography variant="body2">
                      {parsedImport.invalidLines > 0
                        ? `${parsedImport.invalidLines} invalid line(s)`
                        : '0 invalid lines'}
                      {parsedImport.duplicateLines > 0
                        ? `, ${parsedImport.duplicateLines} duplicate(s) skipped`
                        : ''}
                    </Typography>
                  </div>
                )}
              </Box>
            )}

            {previewRows.length > 0 && (
              <Box className={styles.preview}>
                <Typography variant="subtitle2" className={styles.previewTitle}>
                  Preview ({previewRows.length} of{' '}
                  {parsedImport.validRows.length})
                </Typography>
                <div className={styles.previewTable}>
                  <div className={styles.previewHead}>
                    <span>Name</span>
                    <span>Email</span>
                  </div>
                  {previewRows.map((row) => (
                    <div
                      key={`${row.subjectEmail}-${row.sourceLine}`}
                      className={styles.previewRow}
                    >
                      <span>{row.subjectName}</span>
                      <span>{row.subjectEmail}</span>
                    </div>
                  ))}
                </div>
              </Box>
            )}

            <Button
              type="button"
              variant="contained"
              sx={{ mt: 1, mb: 1, width: '100%' }}
              size="large"
              onClick={handleImportSubmit}
              disabled={isSubmitting || parsedImport.validRows.length === 0}
            >
              {isSubmitting
                ? 'Importing...'
                : `Import ${parsedImport.validRows.length} users`}
            </Button>
          </div>
        )}
      </div>
    </MuiModal>
  );
};
